import { useGetBeamerChatEnabled } from 'gcs-common/clients/api/entities/current-user/current-user-api-selectors';
import { BEAMER_CHANNEL_ID } from 'gcs-common/constants/channelConstants';
import { CHAT_ROUTES } from 'gcs-common/constants/routes';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getFirstChannelId } from '../slices/channelList/channelListSelectors';
import { getChannelListChannelIds, getSelectedChannelId } from '../slices/channels/channelsSelectors';
import changeSelectedChannelId from '../slices/channels/channelsThunks/changeSelectedChannelId';
import { resetSelectedMessageIds } from '../slices/messageInput/messageInputSlice';

const useChannelInitialization = () => {
  const isBeamerChatEnabled = useGetBeamerChatEnabled();
  const { routeChannelId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedChannelId = useSelector(getSelectedChannelId) as string | undefined;
  const firstChannelId = useSelector(getFirstChannelId) as string | undefined;
  const allChannels = useSelector(getChannelListChannelIds) as string[];
  const selectedChannelExists = !!selectedChannelId && allChannels.includes(selectedChannelId);
  const { pathname } = useLocation();

  // For handling Beamer:
  // it will be considered a normal channel and the user can be directed to it even if they have no other channels
  // The only condition we need to check if the feature flag is enabled
  // We don't need to look into the list of channels if the beamer exists or not....
  // The beamer channel id will only be in channel list if the feature flag is enabled...
  // This is handled in the beamer useQuery hook

  useEffect(() => {
    if (pathname.startsWith(CHAT_ROUTES.TICKETS)) {
      return;
    }

    if (!selectedChannelId || !selectedChannelExists) {
      const newSelectedChannelId =
        routeChannelId &&
        // The routeChannelId must be in allChannels, and if it’s the Beamer channel, then it must also have Beamer enabled
        allChannels.includes(routeChannelId) &&
        (routeChannelId !== BEAMER_CHANNEL_ID || isBeamerChatEnabled)
          ? routeChannelId
          : firstChannelId;
      if (newSelectedChannelId) {
        dispatch(changeSelectedChannelId({ selectedChannelId: newSelectedChannelId }));
      }
    }

    if (selectedChannelId && selectedChannelId !== routeChannelId) {
      dispatch(resetSelectedMessageIds({ channelId: selectedChannelId }));
      navigate(CHAT_ROUTES.CHANNEL(selectedChannelId));
    }
  }, [
    dispatch,
    firstChannelId,
    allChannels,
    routeChannelId,
    selectedChannelId,
    selectedChannelExists,
    navigate,
    isBeamerChatEnabled,
  ]);
};

export default useChannelInitialization;
